<script setup lang="ts">
/*
 * Карточка-модалка корректирующих мероприятий (КМ) аудита.
 * Поддерживает 3 роли (Руководитель отделения, Исполнитель, Сотрудник СМК),
 * переключение пунктов через табы/сводную таблицу и 4 режима правой панели.
 */

import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify/framework'

import DropZone from '@/@core/components/DropZone.vue'
import AuditCard from '@/modules/audits/stores/useAuditRegisterStore/components/AuditCard.vue'
import TaskCard from '@/modules/tasks/components/TaskCard.vue'
import TaskUnassignedCard from '@/modules/tasks/components/TaskUnassignedCard.vue'
import CreateTaskModal from '@/modules/tasks/components/CreateTaskModal.vue'
import type {
  ICreateTaskInitialData,
  ICreateTaskSubmitPayload,
} from '@/modules/tasks/types/ICreateTaskModalData'
import BridgedEmbeddedChat from '@/components/chat/BridgedEmbeddedChat.vue'
import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import DownloadEntityDocument from '@/components/createdUi/DownloadEntityDocument.vue'
import EntityPaneEar from '@/components/createdUi/EntityPaneEar.vue'
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import EntityAccessDeniedOverlay from '@/components/createdUi/EntityAccessDeniedOverlay.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import EntityFormSummary, { type IEntityFormSummaryItem } from '@/components/createdUi/EntityFormSummary.vue'
import HistoryLogsTable from '@/components/HistoryLogsTable.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditNonconformityFormStore } from '@/modules/audits/stores/useAuditNonconformityFormStore/useAuditNonconformityFormStore'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useEntityChat } from '@/modules/chat-matrix-fast/composables/useEntityChat'
import { useFormEmbeddedLayout } from '@/composables/ui/useFormEmbeddedLayout'
import { useResizablePane } from '@/composables/ui/useResizablePane'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import FileList from '@/modules/files/components/FileList.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useUserStore } from '@/modules/user/stores/userStore'
import { ERouteNames } from '@/router/ERouteNames'
import type { IHistoryLog } from '@/types/IHistoryLog'
import {
  formatDateTime,
  getFullName,
  getInitialsFullName,
} from '@/utils/formatters'
import { isHttpNotFound } from '@/utils/httpErrors'

import type {
  ICorrectiveActionFinding,
} from '../types/IAuditorReportNotComplyDetail'

const props = withDefaults(defineProps<{
  reportId?: number
  auditId?: number
  isGlobal?: boolean
  initialTab?: number
}>(), {
  isGlobal: false,
})

const route = useRoute()
const router = useRouter()
const { xs, smAndDown, mdAndUp } = useDisplay()
const responsiveWidth = computed(() => (mdAndUp.value ? 'w-75' : 'w-100'))
const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const userStore = useUserStore()
const formStore = useAuditNonconformityFormStore()
const auditRegisterStore = useAuditRegisterStore()
const permissionsStore = usePermissionsStore()

const { currentNonconformity, isDirty, activeFindingIndex, currentFinding } = storeToRefs(formStore)
const { currentAudit } = storeToRefs(auditRegisterStore)

const entityReportId = computed(() => {
  if (props.isGlobal)
    return Number(props.reportId) || 0

  return Number(route.params.reportId) || 0
})

const entityAuditId = computed(() => {
  if (props.isGlobal) {
    return Number(props.auditId)
      || Number(currentNonconformity.value?.audit_id)
      || 0
  }

  return Number(route.params.id)
    || Number(currentNonconformity.value?.audit_id)
    || 0
})

const entityLinkTo = computed(() => {
  if (entityAuditId.value && entityReportId.value) {
    return {
      name: ERouteNames.AUDIT_NONCONFORMITY_DETAIL,
      params: { id: String(entityAuditId.value), reportId: String(entityReportId.value) },
    }
  }
  if (entityReportId.value) {
    return {
      name: ERouteNames.AUDITS_NONCONFORMITY_DETAIL,
      params: { reportId: String(entityReportId.value) },
    }
  }
  return undefined
})

const isEditMode = computed(() => Boolean(entityReportId.value))
const formEmbeddedLayout = useFormEmbeddedLayout(isEditMode)

const entityChat = useEntityChat({
  appLabel: 'audit',
  modelName: 'CorrectiveActionReport',
  objectId: computed(() => {
    const id = entityReportId.value
    return typeof id === 'number' && Number.isFinite(id) && id > 0 ? id : null
  }),
  isEnabled: isEditMode,
})

/* Правая панель ресайзер */
const chatPaneResizer = useResizablePane({
  initial: Math.floor(window.innerWidth * 0.42),
  min: 450,
  max: () => Math.floor(window.innerWidth * 0.55),
  storageKey: 'audit-nonconformity-pane-width',
})

/* Режимы правой панели */
type RightPaneMode = 'summary' | 'chat' | 'history' | 'audit_card' | 'tasks'
const rightPaneMode = ref<RightPaneMode>('summary')
const isRightPaneVisible = ref(true)
const isAccessDenied = ref(false)

const dialogContentClass = computed(() => {
  if (!formEmbeddedLayout.contentClass.value) return undefined

  return [
    formEmbeddedLayout.contentClass.value,
    {
      'entity-form-embedded--no-chat': !isRightPaneVisible.value,
    },
  ]
})

const visible = ref(true)
const showCloseConfirm = ref(false)
const showAcceptConfirm = ref(false)

/* Модалка назначения исполнителя */
const showResponsibilityModal = ref(false)
const taskModalInitial = ref<ICreateTaskInitialData | null>(null)
const taskModalTitle = ref('Назначение исполнителя')
const taskModalButtonText = ref('Назначить')


/* Модалка возврата на доработку (руководитель или СМК) */
const showReturnRemarkModal = ref(false)
const returnRemarkText = ref('')
const returnRemarkFiles = ref<File[]>([])
const returnFileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const isReturnFromSmk = ref(false)
const isSubmittingRemark = ref(false)

/* Файлы для загрузки подписанного отчета */
const signedReportFileInput = ref<HTMLInputElement | null>(null)

/* Профиль сотрудника */
const isEmployeeInfoModalOpen = ref(false)
const selectedEmployeeId = ref<string | null>(null)

/* Логи истории текущего пункта (отчёты исполнителя и замечания) */
const historyLogs = computed<IHistoryLog[]>(() => {
  const rawHistory = currentFinding.value?.history || []
  return rawHistory.map((item): IHistoryLog => {
    const isRemark = item.type === 'remark'
    const statusText = item.status
      ? (apiStore.responsibilityEntriesStatuses[item.status as keyof typeof apiStore.responsibilityEntriesStatuses] || item.status)
      : null

    const changesDisplay: Record<string, [string | null, string | null]> = {}
    if (statusText) {
      changesDisplay['Статус'] = [null, statusText]
    }

    return {
      id: item.id,
      cid: null,
      action_display: item.type_display || (isRemark ? 'Замечание' : 'Отчёт'),
      object_repr: '',
      object_display: item.comment || '',
      changes_display: changesDisplay,
      created_by: item.created_by ? {
        id: String(item.created_by.id),
        first_name: item.created_by.first_name,
        last_name: item.created_by.last_name,
        middle_name: item.created_by.middle_name || '',
        avatar: item.created_by.avatar,
        department_name: item.created_by.department_name || null,
        position_name: item.created_by.position_name || null,
      } : null,
      created_at: item.created_at,
    }
  })
})

const getLabelCode = (pointOrLabel?: { label?: string; number?: string } | string | null): string => {
  if (!pointOrLabel) return ''
  if (typeof pointOrLabel === 'object') {
    if (pointOrLabel.number) return String(pointOrLabel.number).trim()
    if (pointOrLabel.label) return getLabelCode(pointOrLabel.label)
    return ''
  }
  const str = String(pointOrLabel).trim()
  const match = str.match(/^([0-9]+(\.[0-9]+)*)/)
  if (match) return match[1]
  return str.split(' ')[0] || ''
}

const getLabelText = (pointOrLabel?: { label?: string; number?: string } | string | null): string => {
  if (!pointOrLabel) return ''
  if (typeof pointOrLabel === 'object') {
    if (pointOrLabel.label) return getLabelText(pointOrLabel.label)
    return ''
  }
  const str = String(pointOrLabel).trim()
  const code = getLabelCode(str)
  if (code && str.startsWith(code)) {
    return str.substring(code.length).trim()
  }
  return str
}

const auditTypePhrase = computed(() => {
  const type = currentAudit.value?.id === entityAuditId.value
    ? currentAudit.value.audit_type
    : null

  if (type === 'unplanned') return 'внеплановой'
  if (type === 'planned') return 'плановой'

  return null
})

const cardTitle = computed(() => {
  const num = entityAuditId.value || currentNonconformity.value?.audit_id
  if (!num) return 'Карточка несоответствий'
  if (!auditTypePhrase.value) return `Карточка несоответствий в проверке №${num}`

  return `Карточка несоответствий в ${auditTypePhrase.value} проверке №${num}`
})

const questionTitle = computed(() => {
  if (!currentFinding.value) return ''
  const code = getLabelCode(currentFinding.value.point)
  return code ? `Вопрос ${code}` : `Вопрос ${activeFindingIndex.value + 1}`
})

const questionRequirementText = computed(() => {
  if (!currentFinding.value) return ''
  const pt = currentFinding.value.point
  if (!pt) return ''
  const text = getLabelText(pt)
  return text || pt.label || ''
})

/* Сводка несоответствия для модалки возврата на доработку */
const returnNonconformitySummaryItems = computed<IEntityFormSummaryItem[]>(() => {
  if (!currentFinding.value) return []

  const managerName = currentNonconformity.value?.responsible_manager
    ? getFullName(currentNonconformity.value.responsible_manager)
    : ''
  const standardsText = (currentNonconformity.value?.standards || [])
    .map(s => s.label)
    .filter(Boolean)
    .join(', ')
  const auditTheme = currentNonconformity.value?.audit_name || currentNonconformity.value?.audit?.name || ''
  const pointCode = getLabelCode(currentFinding.value.point)
  const pointText = getLabelText(currentFinding.value.point)
  const pointFull = [pointCode, pointText].filter(Boolean).join(' ') || currentFinding.value.point?.label || '—'

  return [
    { label: 'Норматив', value: standardsText || 'РЗН стационар' },
    { label: 'Тема проверки', value: auditTheme || '—' },
    { label: 'Предмет проверки', value: pointFull },
    { label: 'Несоответствие', value: currentFinding.value.comment || '—' },
    { label: 'Ответственный', value: managerName || '—' },
  ]
})

/* Сводка задачи исполнителя для модалки возврата на доработку */
const returnTaskSummaryItems = computed<IEntityFormSummaryItem[]>(() => {
  if (!currentFinding.value) return []

  const resp = currentFinding.value.responsibility_entry
  const performerName = resp?.responsible_employee ? getFullName(resp.responsible_employee) : ''
  const taskText = resp?.instructions || '—'
  const deadlineText = resp?.deadline_time ? formatDateTime(resp.deadline_time) : 'Не установлен'
  const performerReport = resp?.last_report || resp?.report || ''

  const items: IEntityFormSummaryItem[] = [
    { label: 'Исполнитель', value: performerName || '—' },
    { label: 'Задача', value: taskText },
    { label: 'Срок выполнения', value: deadlineText },
  ]

  if (performerReport) {
    items.push({ label: 'Отчет', value: performerReport })
  }

  return items
})




const getLevelLabel = (level: number | string | null | undefined): string => {
  const map: Record<number, string> = {
    2: 'Критическое (требует немедленного устранения)',
    1: 'Значительное (устранение в течении 10 дней)',
    0: 'Незначительное (рекомендация для улучшения)',
  }
  if (level === null || level === undefined || level === '') return ''
  const numeric = Number(level)

  return Number.isNaN(numeric) ? String(level) : (map[numeric] ?? String(level))
}

const getCategoryLabel = (category: string | null | undefined): string => {
  const map: Record<string, string> = {
    corrective_action: 'Корректирующее действие',
    warning_action: 'Предупреждающее действие',
    corrective: 'Коррекция',
  }

  return category ? (map[category] || category) : ''
}

/* Бейджи статусов пунктов */
const getFindingStatusChip = (finding: ICorrectiveActionFinding | null) => {
  if (!finding) return { label: '', badgeClass: '' }
  const status = finding.responsibility_entry?.status?.toLowerCase() || ''
  if (!status) return { label: '', badgeClass: '' }

  const statusMap = apiStore.responsibilityEntriesStatuses
  const label = (status === 'completed' || finding.is_worked_off)
    ? 'Завершено'
    : (statusMap[status as keyof typeof statusMap] || status)

  const badgeClass = (status === 'completed' || finding.is_worked_off)
    ? 'worked_off'
    : status

  return { label, badgeClass }
}

const getFindingReportDate = (finding?: ICorrectiveActionFinding | null): string => {
  if (!finding) return ''
  const reportHistory = (finding.history || []).find(h => h.type === 'report')
  if (reportHistory?.created_at) {
    return formatDateTime(reportHistory.created_at)
  }
  if (finding.responsibility_entry?.status === 'completed' && finding.responsibility_entry.created_at) {
    return formatDateTime(finding.responsibility_entry.created_at)
  }
  return ''
}

const summaryHeaders = [
  { title: 'Предмет проверки', key: 'point', sortable: false },
  { title: 'Ответственный исполнитель', key: 'responsible', sortable: false },
  { title: 'Дата отчета', key: 'report_date', sortable: false },
  { title: 'Статус задачи', key: 'status', sortable: false },
]

const summaryRowProps = (data: { item: ICorrectiveActionFinding; index: number }) => ({
  class: [
    'cursor-pointer summary-row',
    data.index === activeFindingIndex.value ? 'active-row' : '',
  ],
  onClick: () => {
    activeFindingIndex.value = data.index
  },
})

/* Проверка просроченности дедлайна */
const isDeadlineOverdue = computed(() => {
  const deadline = currentFinding.value?.responsibility_entry?.deadline_time
  if (!deadline) return false
  return new Date(deadline).getTime() < Date.now()
})

/* Доступные действия над задачей пункта (responsibility_entry.actions / editable_fields) */
const canAssignPerformer = computed(() => {
  const entry = currentFinding.value?.responsibility_entry
  return !entry?.responsible_employee && Boolean(entry?.editable_fields?.includes('responsible_employee'))
})

const canEditPerformer = computed(() => {
  const entry = currentFinding.value?.responsibility_entry
  return Boolean(entry?.responsible_employee) && Boolean(entry?.editable_fields?.includes('responsible_employee'))
})

const canTakeInWork = computed(() => {
  return Boolean(currentFinding.value?.responsibility_entry?.actions?.includes('in-progress'))
})


const canReturnToPerformer = computed(() => {
  // Если доступно действие СМК по возврату отчёта на доработку, скрываем кнопку ответственного
  if (canSmkReturn.value) {
    return false
  }
  return Boolean(currentFinding.value?.responsibility_entry?.actions?.includes('return'))
})

/* Доступные действия над отчетом КМ (currentNonconformity.actions) */
const canSubmitToSmk = computed(() => {
  return Boolean(currentNonconformity.value?.actions?.includes('submit'))
})

/**
 * Доступность возврата отчета КМ на доработку сотрудником СМК.
 * Проверяется действие return-for-revision и право modules.audit.review_corrective_action_report.
 */
const canSmkReturn = computed(() => {
  if (permissionsStore.modules.audit?.review_corrective_action_report !== true) return false
  return Boolean(currentNonconformity.value?.actions?.includes('return-for-revision'))
})

/**
 * Доступность утверждения и приемки отчета КМ сотрудником СМК.
 * Проверяется действие accept и право modules.audit.review_corrective_action_report.
 */
const canSmkAccept = computed(() => {
  if (permissionsStore.modules.audit?.review_corrective_action_report !== true) return false
  return Boolean(currentNonconformity.value?.actions?.includes('accept'))
})

const canUploadReport = computed(() => {
  if (currentNonconformity.value?.status === 'report_uploaded') return false
  return Boolean(currentNonconformity.value?.actions?.includes('upload'))
})

const canPrintReport = computed(() => {
  if (currentNonconformity.value?.status === 'report_uploaded') return false
  return Boolean(currentNonconformity.value?.actions?.includes('print'))
})

const isReportFormedOrUploaded = computed(() => {
  const s = currentNonconformity.value?.status
  return s === 'report_formed' || s === 'report_uploaded'
})

/* Закрытие модалки */

/* Переключение режимов правой панели */
const toggleRightPaneMode = (mode: RightPaneMode) => {
  if (mode === 'chat' && !formEmbeddedLayout.isEmbedded.value) {
    void entityChat.openInSlidingChat()
    return
  }

  if (rightPaneMode.value === mode && isRightPaneVisible.value) {
    isRightPaneVisible.value = false
  } else {
    rightPaneMode.value = mode
    isRightPaneVisible.value = true
    if (mode === 'chat') {
      entityChat.showChat()
    }
  }
}

// Без ушек (мобилка) правая панель открывается под шапкой пункта вместо его тела
const isMobilePaneView = computed(() => !formEmbeddedLayout.isEmbedded.value && isRightPaneVisible.value)

// В мобильном режиме панели заголовок шапки — название открытого раздела, а не карточки
const RIGHT_PANE_TITLES: Record<RightPaneMode, string> = {
  summary: 'Сводный отчет',
  chat: 'Чат',
  history: 'История',
  audit_card: 'Информация о проверке',
  tasks: 'Задачи',
}
const headerTitle = computed(() => (isMobilePaneView.value ? RIGHT_PANE_TITLES[rightPaneMode.value] : cardTitle.value))

const hideRightPane = () => {
  isRightPaneVisible.value = false
}

const closeToList = async () => {
  formStore.resetForm()
  feedbackStore.globalFormTab = null
  if (props.isGlobal) {
    feedbackStore.globalAuditNonconformityReportId = null
    feedbackStore.globalAuditNonconformityAuditId = null
    return
  }
  if (route.name === ERouteNames.AUDITS_NONCONFORMITY_DETAIL) {
    await router.replace({
      name: ERouteNames.AUDITS_NONCONFORMITIES,
    })
    return
  }
  await router.replace({
    name: ERouteNames.AUDIT_NONCONFORMITIES,
    params: { id: String(entityAuditId.value) },
  })
}

const handleClose = () => {
  if (isDirty.value) {
    showCloseConfirm.value = true
    return
  }
  visible.value = false
  closeToList()
}

const onDialogDismissAttempt = useDialogDismissGuard(handleClose)

const handleCloseConfirm = async (value: boolean) => {
  showCloseConfirm.value = false
  if (value) {
    visible.value = false
    await closeToList()
  }
}

const openEmployeeDetail = (id: string) => {
  selectedEmployeeId.value = id
  isEmployeeInfoModalOpen.value = true
}

/* Вычисляемая задача для правой панели TaskCard */
const findingTask = computed(() => {
  const entry = currentFinding.value?.responsibility_entry
  if (!entry) return null
  return {
    ...entry,
    title: entry.title || `Устранение: ${currentFinding.value?.point?.label || 'Пункт аудита'}`,
    taskData: {
      title: entry.title || `Устранение: ${currentFinding.value?.point?.label || 'Пункт аудита'}`,
      instructions: entry.instructions || '',
      deadline_time: entry.deadline_time || null,
      status: entry.status,
      is_high_priority: entry.is_high_priority || false,
    },
    source_type: 'audit' as const,
    url_props: {
      auditId: entityAuditId.value ? Number(entityAuditId.value) : undefined,
      reportId: entityReportId.value ? Number(entityReportId.value) : undefined,
      findingId: currentFinding.value?.id,
    },
  } as any
})

const handleFindingTaskUpdated = async () => {
  if (entityReportId.value) {
    await formStore.fetchById(entityReportId.value)
  }
}

/* Назначение исполнителя (Руководитель) */
const openAssignModal = () => {
  const existing = currentFinding.value?.responsibility_entry
  if (existing?.responsible_employee) {
    taskModalTitle.value = 'Редактирование исполнителя'
    taskModalButtonText.value = 'Сохранить'
    taskModalInitial.value = {
      title: existing.title || `Устранение: ${currentFinding.value?.point?.label || 'Пункт аудита'}`,
      instructions: existing.instructions || '',
      deadline_time: existing.deadline_time || null,
      responsible_employee: existing.responsible_employee,
      is_high_priority: existing.is_high_priority || false,
    }
  } else {
    taskModalTitle.value = 'Назначение исполнителя'
    taskModalButtonText.value = 'Назначить'
    taskModalInitial.value = {
      title: `Устранение: ${currentFinding.value?.point?.label || 'Пункт аудита'}`,
      instructions: currentFinding.value?.comment || '',
      deadline_time: currentFinding.value?.remediation_deadline || null,
    }
  }
  showResponsibilityModal.value = true
}

const handleCustomSubmitTask = async (payload: ICreateTaskSubmitPayload) => {
  if (!currentFinding.value?.id) return null
  const entryId = currentFinding.value.responsibility_entry?.id
  if (!entryId) return null

  await formStore.updateFindingResponsible(
    currentFinding.value.id,
    entryId,
    {
      responsible_employee: payload.responsible_employee ? { id: payload.responsible_employee.id } : undefined,
      responsible_employee_ids: [payload.responsible_employee_id],
      instructions: payload.instructions,
      deadline_time: payload.deadline_time,
      is_high_priority: payload.is_high_priority,
    },
    entityReportId.value,
  )

  // Переключаем правую панель на режим задач
  rightPaneMode.value = 'tasks'
  isRightPaneVisible.value = true

  return currentFinding.value.responsibility_entry
}

/* Взять задачу в работу (Исполнитель или Руководитель) */
const handleTakeInWork = async () => {
  if (!currentFinding.value?.id || !currentFinding.value.responsibility_entry?.id) return
  await formStore.takeFindingTask(
    currentFinding.value.id,
    currentFinding.value.responsibility_entry.id,
    entityReportId.value,
  )
}


/* Возврат на доработку */
const openReturnModal = (isSmk = false) => {
  isReturnFromSmk.value = isSmk
  returnRemarkText.value = ''
  returnRemarkFiles.value = []
  showReturnRemarkModal.value = true
}

const onReturnRemarkFilesUpdate = (files: File[]) => {
  returnRemarkFiles.value = files
}

const submitReturnRemark = async () => {
  if (!returnRemarkText.value.trim()) {
    feedbackStore.showToast({ type: 'warning', message: 'Введите комментарий к доработке' })
    return
  }

  isSubmittingRemark.value = true
  try {
    if (isReturnFromSmk.value) {
      const entryId = currentFinding.value?.responsibility_entry?.id || 0
      await formStore.returnReportForRevision(
        entityReportId.value,
        entryId ? [{ responsibility_entry_id: entryId, comment: returnRemarkText.value.trim() }] : [],
        returnRemarkText.value.trim(),
      )
    } else {
      if (!currentFinding.value?.id || !currentFinding.value.responsibility_entry?.id) return
      await formStore.addFindingRemark(
        currentFinding.value.id,
        currentFinding.value.responsibility_entry.id,
        returnRemarkText.value.trim(),
        entityReportId.value,
      )
    }
    showReturnRemarkModal.value = false
    returnRemarkFiles.value = []
  } finally {
    isSubmittingRemark.value = false
  }
}

/* Согласование отчета сотрудником СМК */
const handleAcceptReport = async () => {
  await formStore.acceptReport(entityReportId.value)
  showAcceptConfirm.value = false
}

const isSubmittingReport = ref(false)

/* Отправка в СМК */
const handleSubmitToSmk = async () => {
  isSubmittingReport.value = true
  try {
    await formStore.submitReport(entityReportId.value)
  } finally {
    isSubmittingReport.value = false
  }
}

/* Загрузка подписанного отчета */
const triggerSignedReportUpload = () => {
  signedReportFileInput.value?.click()
}

const onSignedReportFileSelected = async (e: Event) => {
  const target = e.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  await formStore.uploadReportFile(entityReportId.value, file)
  if (signedReportFileInput.value) signedReportFileInput.value.value = ''
}

/* Скачивание DOCX */
const handleDownloadDocx = async () => {
  await formStore.printReportDocx(entityReportId.value)
}

watch(visible, val => {
  if (!val) closeToList()
})

onMounted(async () => {
  if (!formEmbeddedLayout.isEmbedded.value) {
    isRightPaneVisible.value = false
  }
  if (!apiStore.employees?.length) {
    await apiStore.fetchAllEmployees()
  }
  if (entityReportId.value) {
    try {
      isAccessDenied.value = false
      await formStore.fetchById(entityReportId.value)
    } catch (e) {
      if (isHttpNotFound(e))
        isAccessDenied.value = true
    }
  }
  if (entityAuditId.value && currentAudit.value?.id !== entityAuditId.value) {
    await auditRegisterStore.fetchAuditById(entityAuditId.value)
  }

  if (props.initialTab !== undefined && currentNonconformity.value?.findings?.length) {
    activeFindingIndex.value = Math.min(props.initialTab, currentNonconformity.value.findings.length - 1)
  }
})

onUnmounted(() => {
  formStore.resetForm()
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="formEmbeddedLayout.isEmbedded.value && visible"
      class="entity-form-backdrop"
    />
  </Teleport>

  <VDialog
    v-model="visible"
    :z-index="2400"
    :max-width="formEmbeddedLayout.maxWidth.value"
    :content-class="dialogContentClass"
    :scrim="!formEmbeddedLayout.isEmbedded.value"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <!-- Кнопки-ушки в правом верхнем углу (Teleport) -->
    <Teleport to="body">
      <div
        v-if="formEmbeddedLayout.isEmbedded.value && visible"
        class="entity-form-actions"
      >
        <!-- Режим 5: Задачи (TaskCard) -->
        <EntityPaneEar
          kind="tasks"
          :active="isRightPaneVisible && rightPaneMode === 'tasks'"
          @click="toggleRightPaneMode('tasks')"
        />

        <!-- Режим 2: Matrix Чат -->
        <EntityPaneEar
          v-if="entityChat.isAvailable.value"
          kind="chat"
          :active="isRightPaneVisible && rightPaneMode === 'chat'"
          @click="toggleRightPaneMode('chat')"
        />

        <!-- Режим 4: Карточка аудита -->
        <VTooltip location="bottom" text="Информация о проверке">
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              icon="ri-search-eye-line"
              :color="isRightPaneVisible && rightPaneMode === 'audit_card' ? 'secondary' : 'primary'"
              class="entity-form-action-btn"
              @click="toggleRightPaneMode('audit_card')"
            />
          </template>
        </VTooltip>

        <!-- Режим 1: Сводный отчет -->
        <VTooltip location="bottom" text="Сводный отчет">
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              icon="ri-table-line"
              :color="isRightPaneVisible && rightPaneMode === 'summary' ? 'secondary' : 'primary'"
              class="entity-form-action-btn"
              @click="toggleRightPaneMode('summary')"
            />
          </template>
        </VTooltip>

        <!-- Режим 3: История изменений -->
        <VTooltip
          v-if="permissionsStore.canViewChangeHistory"
          location="bottom"
          :text="isRightPaneVisible && rightPaneMode === 'history' ? 'Скрыть историю' : 'Показать историю'"
        >
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              icon
              color="primary"
              class="entity-form-action-btn"
              aria-label="История"
              @click="toggleRightPaneMode('history')"
            >
              <span class="history-toggle-icon">
                <VIcon icon="ri-history-line" />
                <span
                  v-if="isRightPaneVisible && rightPaneMode === 'history'"
                  class="history-toggle-slash"
                  aria-hidden="true"
                />
              </span>
            </VBtn>
          </template>
        </VTooltip>

        <!-- Печатная форма и отчёты — по аудиту: у несоответствия своей карточки на бэке нет -->
        <DownloadEntityDocument
          v-if="entityAuditId"
          variant="ear"
          :path="`/api/audit/audit/${entityAuditId}/document/`"
          :fallback-name="`audit_${entityAuditId}`"
        />
        <ReportTemplateMenu v-if="entityAuditId" object-type="audit" :object-id="entityAuditId" variant="ear" />

        <!-- Копирование ссылки на сущность -->
        <CopyEntityLink
          v-if="entityLinkTo"
          variant="ear"
          :to="entityLinkTo"
        />

        <!-- Закрытие модалки -->
        <VTooltip location="bottom" text="Закрыть">
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              icon="ri-close-line"
              color="primary"
              class="entity-form-action-btn"
              @click="handleClose"
            />
          </template>
        </VTooltip>
      </div>
    </Teleport>

    <VCard :class="{ 'entity-form-card h-100': formEmbeddedLayout.isEmbedded.value }">
      <div :class="formEmbeddedLayout.isEmbedded.value ? 'entity-form-body d-flex h-100 overflow-hidden' : ((isMobilePaneView) ? 'entity-form-mobile-pane-body' : undefined)">
        <!-- Панель кнопок разделов на мобильных устройствах НАД названием; закреплена при прокрутке -->
        <div
          v-if="!formEmbeddedLayout.isEmbedded.value"
          class="entity-form-mobile-actions d-flex align-center justify-space-evenly w-100"
        >
          <VTooltip location="bottom" text="Задачи">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                icon="ri-list-check"
                variant="text"
                :color="isRightPaneVisible && rightPaneMode === 'tasks' ? 'primary' : 'secondary'"
                @click="toggleRightPaneMode('tasks')"
              />
            </template>
          </VTooltip>
          <VTooltip
            v-if="entityChat.isAvailable.value"
            location="bottom"
            text="Чат"
          >
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                icon="ri-chat-1-line"
                variant="text"
                :color="isRightPaneVisible && rightPaneMode === 'chat' ? 'primary' : 'secondary'"
                @click="toggleRightPaneMode('chat')"
              />
            </template>
          </VTooltip>
          <VTooltip location="bottom" text="Информация о проверке">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                icon="ri-search-eye-line"
                variant="text"
                :color="isRightPaneVisible && rightPaneMode === 'audit_card' ? 'primary' : 'secondary'"
                @click="toggleRightPaneMode('audit_card')"
              />
            </template>
          </VTooltip>
          <VTooltip location="bottom" text="Сводный отчет">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                icon="ri-table-line"
                variant="text"
                :color="isRightPaneVisible && rightPaneMode === 'summary' ? 'primary' : 'secondary'"
                @click="toggleRightPaneMode('summary')"
              />
            </template>
          </VTooltip>
          <VTooltip
            v-if="permissionsStore.canViewChangeHistory"
            location="bottom"
            :text="isRightPaneVisible && rightPaneMode === 'history' ? 'Скрыть историю' : 'Показать историю'"
          >
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                icon
                variant="text"
                :color="isRightPaneVisible && rightPaneMode === 'history' ? 'primary' : 'secondary'"
                aria-label="История"
                @click="toggleRightPaneMode('history')"
              >
                <span class="history-toggle-icon">
                  <VIcon icon="ri-history-line" />
                  <span
                    v-if="isRightPaneVisible && rightPaneMode === 'history'"
                    class="history-toggle-slash"
                    aria-hidden="true"
                  />
                </span>
              </VBtn>
            </template>
          </VTooltip>
          <DownloadEntityDocument
            v-if="entityAuditId"
            variant="inline"
            :path="`/api/audit/audit/${entityAuditId}/document/`"
            :fallback-name="`audit_${entityAuditId}`"
          />
          <ReportTemplateMenu v-if="entityAuditId" object-type="audit" :object-id="entityAuditId" variant="inline" />
          <CopyEntityLink
            v-if="entityAuditId && entityReportId"
            variant="inline"
            :to="{
              name: ERouteNames.AUDIT_NONCONFORMITY_DETAIL,
              params: { id: entityAuditId, reportId: entityReportId },
            }"
          />
          <CustomCloseButton :close-action="handleClose" />
        </div>
        <!-- ========================================== -->
        <!-- ЛЕВАЯ ПАНЕЛЬ: ФОРМА ПУНКТА НЕСООТВЕТСТВИЯ -->
        <!-- ========================================== -->
        <div
          :class="formEmbeddedLayout.isEmbedded.value ? 'entity-form-pane flex-grow-1 d-flex flex-column position-relative' : 'd-flex flex-column flex-grow-1 position-relative'"
        >
          <EntityAccessDeniedOverlay v-if="isAccessDenied" variant="form" />
          <!-- Шапка модалки -->
          <VCardTitle
            class="d-flex flex-column pa-6 pb-2"
            :class="xs ? 'px-4 py-3' : 'pa-6 pb-2'"
            :style="isAccessDenied ? { position: 'relative', zIndex: 6 } : undefined"
          >

            <div class="form_card d-flex justify-space-between align-center w-100">
              <div class="form_card__title">
                <span class="text-h4 font-weight-semibold">{{ headerTitle }}</span>
              </div>
            </div>
          </VCardTitle>

          <!-- Тело активного пункта (структура как в TicketForm.vue) -->
          <div v-show="!isMobilePaneView" v-if="currentNonconformity && currentFinding" class="entity-form-scroll-body flex-grow-1">
            <!-- Вкладки пунктов несоответствий -->
            <VTabs v-model="activeFindingIndex" class="custom-finding-tabs border-0 mb-4">
              <VTab
                v-for="(finding, index) in currentNonconformity?.findings || []"
                :key="finding.id || index"
                :value="index"
              >
                {{ getLabelCode(finding.point) || `${index + 1}` }}
              </VTab>
            </VTabs>

            <div class="d-flex flex-column gap-6">
              <!-- Заголовок вопроса и чип статуса -->
              <div class="d-flex align-center justify-space-between">
                <span class="text-h5 font-weight-semibold text-high-emphasis">
                  {{ questionTitle }}
                </span>
                <VChip
                  v-if="currentNonconformity?.status_label || currentNonconformity?.status"
                  class="status-badge"
                  :class="currentNonconformity.status"
                  size="small"
                >
                  {{ currentNonconformity.status_label || currentNonconformity.status }}
                </VChip>
              </div>

              <!-- Текст требования / пункта -->
              <div v-if="questionRequirementText" class="text-body-1 text-high-emphasis">
                {{ questionRequirementText }}
              </div>

              <!-- Наблюдение аудитора -->
              <VTextarea
                :model-value="currentFinding.comment"
                readonly
                density="comfortable"
                rows="3"
                variant="outlined"
              >
                <template #label>
                  <span>Наблюдение аудитора</span>
                </template>
              </VTextarea>

              <!-- Рекомендации аудитора -->
              <VTextarea
                v-if="currentFinding.remediation_recommendations"
                :model-value="currentFinding.remediation_recommendations"
                readonly
                density="comfortable"
                rows="3"
                variant="outlined"
              >
                <template #label>
                  <span>Рекомендации</span>
                </template>
              </VTextarea>

              <!-- Рекомендованный срок устранения -->
              <VTextField
                v-if="currentFinding.remediation_deadline"
                :model-value="formatDateTime(currentFinding.remediation_deadline)"
                class="w-100"
                variant="outlined"
                readonly
              >
                <template #label>
                  <span>Срок устранения</span>
                </template>
                <template #append-inner>
                  <VIcon icon="ri-calendar-line" size="20" />
                </template>
              </VTextField>

              <!-- Прикрепленные файлы наблюдения -->
              <FileList
                v-if="currentFinding.files?.length"
                :items="currentFinding.files"
              />

              <!-- Градация и Категория действия -->
              <div class="d-flex gap-4" :class="{ 'flex-column': smAndDown }">
                <VTextField
                  :model-value="getLevelLabel(currentFinding.not_comply_level)"
                  class="flex-grow-1"
                  variant="outlined"
                  readonly
                >
                  <template #label>
                    <span>Градация</span>
                  </template>
                </VTextField>
                <VTextField
                  :model-value="getCategoryLabel(currentFinding.not_comply_category)"
                  class="flex-grow-1"
                  variant="outlined"
                  readonly
                >
                  <template #label>
                    <span>Категория действия</span>
                  </template>
                </VTextField>
              </div>

              <!-- Разделитель: КОРРЕКТИРУЮЩИЕ МЕРОПРИЯТИЯ -->
              <CustomDivider title="КОРРЕКТИРУЮЩИЕ МЕРОПРИЯТИЯ" />

              <!-- Поле Ответственный (Руководитель отделения) -->
              <VTextField
                :model-value="currentNonconformity.responsible_manager
                  ? getFullName(currentNonconformity.responsible_manager)
                  : 'Не назначен'"
                :class="responsiveWidth"
                variant="outlined"
                readonly
              >
                <template #label>
                  <span>Ответственный</span>
                </template>
              </VTextField>



              <!-- Комментарий к доработке (при наличии замечания руководителя/СМК) -->
              <VTextarea
                v-if="currentFinding.responsibility_entry?.last_remark?.comment"
                :model-value="currentFinding.responsibility_entry.last_remark.comment"
                readonly
                density="comfortable"
                rows="3"
                variant="outlined"
              >
                <template #label>
                  <span>
                    Комментарий к доработке{{
                      currentFinding.responsibility_entry.last_remark.created_by
                        ? ` от ${getInitialsFullName(currentFinding.responsibility_entry.last_remark.created_by)}`
                        : ''
                    }}
                  </span>
                </template>
              </VTextarea>
            </div>
          </div>

          <!-- Футер левой панели: Данные аудитора и Кнопки действий ролей (эталонная разметка TicketForm.vue) -->
          <footer
            v-if="!isMobilePaneView && currentNonconformity && currentFinding"
            class="d-flex flex-column gap-2 body-2 pa-6 pt-0"
          >
            <CustomDivider />

            <div
              class="d-flex gap-4"
              :class="xs ? 'flex-column' : 'align-center justify-space-between'"
            >
              <!-- Слева: Аудитор и время фиксации -->
              <div class="d-flex flex-column gap-2" :class="{ 'order-2': xs, 'text-no-wrap': !xs }">
                <div class="d-flex gap-2 align-center">
                  <VIcon icon="ri-user-3-line" size="16" class="flex-shrink-0" />
                  <span
                    class="cursor-pointer"
                    @click="currentFinding.created_by?.id && openEmployeeDetail(currentFinding.created_by.id)"
                  >
                    Аудитор: {{ getFullName(currentFinding.created_by ?? {}) }}
                  </span>
                  <VTooltip v-if="currentFinding.created_by?.department_name">
                    <template #activator="{ props: actProps }">
                      <VIcon icon="ri-information-line" size="10" v-bind="actProps" class="flex-shrink-0" />
                    </template>
                    <div>
                      {{ currentFinding.created_by.department_name }}
                    </div>
                  </VTooltip>
                </div>

                <div class="d-flex gap-2 align-center">
                  <VIcon icon="ri-time-line" size="16" class="flex-shrink-0" />
                  <span>Зафиксировано: {{ formatDateTime(currentFinding.created_at) }}</span>
                </div>
              </div>

              <!-- Справа: Кнопки действий на основе флагов бэкенда (эталонные стили TicketForm.vue) -->
              <div class="d-flex gap-2 flex-shrink-0" :class="xs ? 'flex-column order-1 w-100' : 'align-center'">
                <!-- Назначить исполнителя -->
                <VBtn
                  v-if="canAssignPerformer"
                  :block="xs"
                  variant="outlined"
                  prepend-icon="ri-user-add-line"
                  @click="openAssignModal"
                >
                  + Назначить исполнителя
                </VBtn>

                <!-- Редактировать исполнителя -->
                <VBtn
                  v-if="canEditPerformer"
                  :block="xs"
                  variant="outlined"
                  prepend-icon="ri-edit-line"
                  @click="openAssignModal"
                >
                  Редактировать исполнителя
                </VBtn>

                <!-- Руководитель: Вернуть на доработку -->
                <VBtn
                  v-if="canReturnToPerformer"
                  :block="xs"
                  variant="outlined"
                  prepend-icon="ri-corner-down-left-fill"
                  @click="openReturnModal(false)"
                >
                  Вернуть на доработку
                </VBtn>

                <!-- СМК: Вернуть на доработку -->
                <VBtn
                  v-if="canSmkReturn"
                  :block="xs"
                  variant="outlined"
                  prepend-icon="ri-corner-down-left-fill"
                  @click="openReturnModal(true)"
                >
                  Вернуть на доработку
                </VBtn>

                <!-- Ответственный: Отправка отчета в СМК -->
                <VBtn
                  v-if="canSubmitToSmk"
                  :block="xs"
                  color="primary"
                  variant="flat"
                  prepend-icon="ri-check-line"
                  :loading="isSubmittingReport"
                  @click="handleSubmitToSmk"
                >
                  Контроль СМК
                </VBtn>
              </div>
            </div>
          </footer>
        </div>

        <!-- Разделитель между левой и правой панелями -->
        <div
          v-if="formEmbeddedLayout.isEmbedded.value && isRightPaneVisible"
          class="entity-form-resizer"
          :class="{ 'is-resizing': chatPaneResizer.isResizing.value }"
          @mousedown="chatPaneResizer.startResize"
        />

        <!-- ========================================== -->
        <!-- ПРАВАЯ ПАНЕЛЬ: 4 РЕЖИМА                     -->
        <!-- ========================================== -->
        <div
          v-if="isRightPaneVisible"
          :class="formEmbeddedLayout.isEmbedded.value ? 'entity-form-chat-pane d-flex flex-column h-100' : 'd-flex flex-column flex-grow-1 h-100'"
          :style="formEmbeddedLayout.isEmbedded.value ? { flexBasis: `${chatPaneResizer.width.value}px` } : undefined"
        >
          <!-- РЕЖИМ 5: ЗАДАЧА ТЕКУЩЕГО ПУНКТА (TaskCard) -->
          <div
            :class="rightPaneMode === 'tasks' ? 'd-flex flex-column h-100 flex-grow-1 min-h-0' : 'd-none'"
          >
            <div
              v-if="currentFinding?.responsibility_entry?.responsible_employee && findingTask"
              class="h-100 overflow-hidden pa-0 d-flex flex-column flex-grow-1"
            >
              <TaskCard
                class="flex-grow-1"
                :task="findingTask"
                @updated="handleFindingTaskUpdated"
              />
            </div>
            <TaskUnassignedCard
              v-else
              :title="`Устранение: ${currentFinding?.point?.label || 'Пункт аудита'}`"
              hint="Для этого пункта пока не назначен исполнитель"
              :can-assign="canAssignPerformer"
              @assign="openAssignModal"
            />
          </div>

          <!-- РЕЖИМ 1: СВОДНЫЙ ОТЧЕТ (По умолчанию) -->
          <div
            :class="rightPaneMode === 'summary' ? 'd-flex flex-column h-100 flex-grow-1 min-h-0' : 'd-none'"
          >
            <VCardTitle v-if="formEmbeddedLayout.isEmbedded.value" class="d-flex pa-6 pb-2" :class="xs ? 'px-4 py-3' : 'pa-6 pb-2'">
              <div class="d-flex justify-space-between align-center w-100">
                <span class="text-h4 font-weight-semibold">Сводный отчет</span>
                <div class="d-flex align-center gap-1">
                  <VTooltip location="bottom" text="Свернуть">
                    <template #activator="{ props: tipProps }">
                      <IconBtn
                        v-bind="tipProps"
                        class="ms-2"
                        @click="hideRightPane"
                      >
                        <VIcon icon="ri-play-fill" />
                      </IconBtn>
                    </template>
                  </VTooltip>
                </div>
              </div>
            </VCardTitle>

            <div class="entity-form-scroll-body flex-grow-1 pb-6">
              <!-- Таблица сводного отчета через стандартный VDataTable -->
              <VDataTable
                :headers="summaryHeaders"
                :items="currentNonconformity?.findings || []"
                class="custom-data-table no-row-gaps cursor-pointer-table border rounded"
                hide-default-footer
                :items-per-page="-1"
                :row-props="summaryRowProps"
              >
                <template #[`item.point`]="{ item, index }">
                  <span class="font-weight-medium summary-cell-text">
                    {{ getLabelCode(item.point) || `${index + 1}` }}
                  </span>
                </template>

                <template #[`item.responsible`]="{ item }">
                  <span class="summary-cell-text">
                    {{ item.responsibility_entry?.responsible_employee
                      ? getInitialsFullName(item.responsibility_entry.responsible_employee)
                      : (currentNonconformity?.responsible_manager ? getInitialsFullName(currentNonconformity.responsible_manager) : '')
                    }}
                  </span>
                </template>

                <template #[`item.report_date`]="{ item }">
                  <span class="summary-cell-text">
                    {{ getFindingReportDate(item) }}
                  </span>
                </template>

                <template #[`item.status`]="{ item }">
                  <VChip
                    v-if="getFindingStatusChip(item).label"
                    class="status-badge"
                    :class="getFindingStatusChip(item).badgeClass"
                    size="x-small"
                  >
                    {{ getFindingStatusChip(item).label }}
                  </VChip>
                </template>
              </VDataTable>

              <!-- Кнопка прикрепления подписанного отчета -->
              <div v-if="isReportFormedOrUploaded" class="mt-6 d-flex flex-column gap-3">
                <input
                  ref="signedReportFileInput"
                  type="file"
                  accept="application/pdf,image/*"
                  class="d-none"
                  @change="onSignedReportFileSelected"
                >

                <VBtn
                  v-if="canUploadReport"
                  variant="outlined"
                  color="primary"
                  prepend-icon="ri-attachment-2"
                  :block="xs"
                  :class="{ 'align-self-end': !xs }"
                  @click="triggerSignedReportUpload"
                >
                  Прикрепить подписанный отчет
                </VBtn>

                <!-- Отображение прикрепленного файла -->
                <FileList
                  v-if="currentNonconformity?.files?.length"
                  :items="currentNonconformity.files"
                />
              </div>
            </div>
          </div>

          <div
            :class="rightPaneMode === 'chat' ? 'd-flex flex-column h-100 flex-grow-1 min-h-0' : 'd-none'"
          >
            <BridgedEmbeddedChat
              v-if="entityChat.isAvailable.value"
              :chat-id="entityChat.chatId.value"
              :connect-error="entityChat.connectError.value"
              class="h-100"
              @hide-chat="hideRightPane"
            />
          </div>

          <!-- РЕЖИМ 3: ИСТОРИЯ ИЗМЕНЕНИЙ -->
          <div
            :class="permissionsStore.canViewChangeHistory && rightPaneMode === 'history' ? 'd-flex flex-column h-100 flex-grow-1 min-h-0' : 'd-none'"
          >
            <VCardTitle v-if="formEmbeddedLayout.isEmbedded.value" class="d-flex pa-6 pb-2" :class="xs ? 'px-4 py-3' : 'pa-6 pb-2'">
              <div class="d-flex justify-space-between align-center w-100">
                <span class="text-h4 font-weight-semibold">История</span>
                <div class="d-flex align-center gap-1">
                  <VTooltip location="bottom" text="Свернуть">
                    <template #activator="{ props: tipProps }">
                      <IconBtn
                        v-bind="tipProps"
                        class="ms-2"
                        @click="hideRightPane"
                      >
                        <VIcon icon="ri-play-fill" />
                      </IconBtn>
                    </template>
                  </VTooltip>
                </div>
              </div>
            </VCardTitle>
            <div class="entity-form-scroll-body flex-grow-1 pb-6">
              <HistoryLogsTable
                :logs="historyLogs"
                @click-user="openEmployeeDetail"
              />
            </div>
          </div>

          <!-- РЕЖИМ 4: КАРТОЧКА АУДИТА -->
          <div
            :class="rightPaneMode === 'audit_card' ? 'd-flex flex-column h-100 flex-grow-1 min-h-0' : 'd-none'"
          >
            <VCardTitle v-if="formEmbeddedLayout.isEmbedded.value" class="d-flex pa-6 pb-2" :class="xs ? 'px-4 py-3' : 'pa-6 pb-2'">
              <div class="d-flex justify-space-between align-center w-100">
                <span class="text-h4 font-weight-semibold">Информация о проверке</span>
                <div class="d-flex align-center gap-1">
                  <VTooltip location="bottom" text="Свернуть">
                    <template #activator="{ props: tipProps }">
                      <IconBtn
                        v-bind="tipProps"
                        class="ms-2"
                        @click="hideRightPane"
                      >
                        <VIcon icon="ri-play-fill" />
                      </IconBtn>
                    </template>
                  </VTooltip>
                </div>
              </div>
            </VCardTitle>
            <div class="entity-form-scroll-body flex-grow-1 pb-6">
              <AuditCard
                v-if="entityAuditId"
                :audit-id="entityAuditId"
              />
            </div>
          </div>

          <!-- Футер правой панели (общий для всех режимов: сводный отчет, чат, история, карточка аудита; скрыт на вкладке задач, так как TaskCard имеет собственный футер) -->
          <footer
            v-if="rightPaneMode !== 'tasks'"
            class="d-flex flex-column gap-2 body-2 pa-6 pt-0"
          >
            <CustomDivider />

            <div
              class="d-flex gap-4"
              :class="xs ? 'flex-column' : 'align-center justify-space-between'"
            >
              <div class="d-flex flex-column gap-2" :class="{ 'order-2': xs, 'text-no-wrap': !xs }">
                <div v-if="currentNonconformity?.department?.name" class="d-flex gap-2 align-center">
                  <VIcon icon="ri-building-line" size="16" class="flex-shrink-0" />
                  <span>{{ currentNonconformity.department.name }}</span>
                </div>
                <div class="d-flex gap-2 align-center">
                  <VIcon icon="ri-time-line" size="16" class="flex-shrink-0" />
                  <span>Проверка завершена: {{ formatDateTime(currentNonconformity?.audit?.actual_end || currentNonconformity?.created_at) }}</span>
                </div>
              </div>

              <div class="d-flex gap-2 flex-shrink-0" :class="xs ? 'flex-column order-1 w-100' : 'align-center'">
                <!-- Кнопка скачивания для подписания -->
                <VBtn
                  v-if="canPrintReport"
                  :block="xs"
                  variant="outlined"
                  prepend-icon="ri-download-line"
                  @click="handleDownloadDocx"
                >
                  Скачать для подписания
                </VBtn>

                <!-- Кнопка согласования отчета СМК -->
                <VBtn
                  v-if="canSmkAccept"
                  :block="xs"
                  color="primary"
                  variant="flat"
                  prepend-icon="ri-check-line"
                  @click="showAcceptConfirm = true"
                >
                  Согласовать отчет
                </VBtn>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </VCard>
  </VDialog>

  <!-- Модалка создания/назначения задачи -->
  <CreateTaskModal
    v-model="showResponsibilityModal"
    :modal-title="taskModalTitle"
    :submit-button-text="taskModalButtonText"
    :initial-data="taskModalInitial"
    :custom-submit="handleCustomSubmitTask"
  />


  <!-- Модалка комментария к доработке (Руководитель или СМК) -->
  <VDialog
    v-model="showReturnRemarkModal"
    max-width="40rem"
    persistent
    content-class="entity-form-menu"
  >
    <VCard class="pa-6 d-flex flex-column gap-6">
      <div class="d-flex justify-space-between align-center">
        <span class="text-h4 font-weight-semibold">Комментарий к доработке</span>
        <CustomCloseButton :close-action="() => { showReturnRemarkModal = false }" />
      </div>

      <div class="d-flex flex-column gap-4">
        <!-- Сводка несоответствия -->
        <EntityFormSummary :items="returnNonconformitySummaryItems" />

        <CustomDivider title="Задача" />

        <!-- Сводка задачи исполнителя -->
        <EntityFormSummary :items="returnTaskSummaryItems" />

        <!-- Прикрепленные исполнителем файлы (если есть) -->
        <div v-if="currentFinding?.responsibility_entry?.files?.length" class="d-flex flex-column ga-1">
          <span class="text-body-1 entity-form-summary__label">Файлы отчета исполнителя:</span>
          <FileList :items="currentFinding.responsibility_entry.files" />
        </div>

        <CustomDivider />

        <!-- Поле ввода комментария к доработке с кнопкой загрузки файлов -->
        <div class="d-flex flex-column gap-2">
          <div class="d-flex gap-2">
            <div class="d-flex w-100">
              <VTextarea
                v-model="returnRemarkText"
                density="comfortable"
                rows="3"
                variant="outlined"
                persistent-placeholder
                placeholder="Опишите, что необходимо скорректировать"
              >
                <template #label>
                  <span>Комментарий к доработке<sup class="required-label">*</sup></span>
                </template>
              </VTextarea>
            </div>

            <VTooltip>
              <template #activator="{ props: activatorProps }">
                <VIconBtn
                  :rounded="false"
                  :size="34"
                  class="rounded-sm flex-grow-0 flex-shrink-0"
                  color="secondary"
                  icon="ri-download-2-line"
                  variant="outlined"
                  v-bind="activatorProps"
                  @click="returnFileAttachRef?.openDialog()"
                />
              </template>

              <div>
                Загрузить файлы
              </div>
            </VTooltip>
          </div>

          <div
            v-if="!smAndDown"
            class="text-body-2 paste-hint"
          >
            или вставьте файлы из буфера (Ctrl+V)
          </div>

          <FileAttach
            ref="returnFileAttachRef"
            :files="returnRemarkFiles"
            accept="*"
            @update:files="onReturnRemarkFilesUpdate"
          />
        </div>
      </div>

      <div class="d-flex justify-end gap-2">
        <VBtn
          color="primary"
          :loading="isSubmittingRemark"
          :disabled="!returnRemarkText.trim()"
          @click="submitReturnRemark"
        >
          Отправить
        </VBtn>
      </div>
    </VCard>
  </VDialog>



  <!-- Диалог подтверждения согласования отчета СМК -->
  <ConfirmDialog
    v-model:is-dialog-visible="showAcceptConfirm"
    confirmation-question="Согласовать отчет по корректирующим мероприятиям?"
    confirmation-text="Отчет перейдет в статус сформированного для подписания"
    confirm-btn="Согласовать"
    reject-btn="Отмена"
    @confirm="(ok: boolean) => { if (ok) handleAcceptReport(); else showAcceptConfirm = false }"
  />

  <!-- Диалог несохраненных изменений при закрытии -->
  <ConfirmDialog
    v-model:is-dialog-visible="showCloseConfirm"
    confirmation-question="Есть несохранённые изменения. Закрыть карточку?"
    confirmation-text="Данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отмена"
    @confirm="handleCloseConfirm"
  />

  <!-- Профиль сотрудника -->
  <EmployeeProfileSidebar
    v-model="isEmployeeInfoModalOpen"
    :employee-id="selectedEmployeeId || ''"
  />
</template>

<style scoped lang="scss">
:deep(.custom-finding-tabs) {
  border-block-end: none !important;
  border-bottom: none !important;
}

/* Стандартные стили таблиц custom-data-table проекта (как в ResponsibilityEntriesTable / AuditRegisterTable) */
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

/* Активная строка сводной таблицы (соответствующая открытой вкладке) */
:deep(.custom-data-table .v-data-table__tr.active-row) {
  background-color: rgba(var(--v-theme-primary), 0.24) !important;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.3) !important;
  }

  .summary-cell-text {
    color: rgb(var(--v-theme-primary)) !important;
    font-weight: 500 !important;
  }
}

.entity-form-summary__label {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
  font-weight: 500;
}

.paste-hint {
  color: rgba(var(--v-theme-text-primary), 0.55);
}
</style>
